import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  HStack,
  Text,
  FormControl,
  FormLabel,
  Select,
} from "@chakra-ui/react";
import DataTable from "../AppDataTable";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";

// สีตามประเภท (จาก chartThemes.soft)
const typeColors = {
  POULTRY: "#ff8fab", // ชมพู
  CATTLE: "#74a892", // เขียว
  PIG: "#a2d2ff", // ฟ้า
};

const VaccineReceiptDashboard = () => {
  const { sessionId } = useAuth();
  const [receiptData, setReceiptData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [hoveredType, setHoveredType] = useState(null);

  // ดึงข้อมูลจาก API
  useEffect(() => {
    const fetchReceipts = async () => {
      setIsLoading(true);
      try {
        const response = await apiService({
          method: "POST",
          url: "/getVaccineReceiveSummary",
          data: {
            Month: "2025-04", // สมมติเดือนเมษายน 2568
          },
          sessionId: sessionId || "",
        });
        if (response.result === "success" && response.summary) {
          // รวมยอดวัคซีนเดียวกันจากคลังต่าง ๆ
          const groupedData = response.summary.reduce((acc, item) => {
            const key = `${item.LivestockType}_${item.VaccineNameTH}`;
            if (!acc[key]) {
              acc[key] = {
                LivestockType: item.LivestockType,
                VaccineNameTH: item.VaccineNameTH,
                VaccineNameEN: item.VaccineNameEN,
                TotalQuantity: 0,
                TotalValue: 0,
              };
            }
            acc[key].TotalQuantity += Number(item.TotalQuantity);
            acc[key].TotalValue += Number(item.TotalValue);
            return acc;
          }, {});

          const receipts = Object.values(groupedData);
          setReceiptData(receipts);

          // สร้างยอดรวมตามประเภท
          const summary = receipts.reduce((acc, item) => {
            const type = item.LivestockType;
            if (!acc[type]) {
              acc[type] = {
                LivestockType: type,
                Quantity: 0,
                Value: 0,
              };
            }
            acc[type].Quantity += item.TotalQuantity;
            acc[type].Value += item.TotalValue;
            return acc;
          }, {});
          setSummaryData(Object.values(summary));
        } else {
          setReceiptData([]);
          setSummaryData([]);
          console.error("Failed to fetch receipts:", response.error);
        }
      } catch (error) {
        console.error("Error fetching receipts:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (sessionId) fetchReceipts();
  }, [sessionId]);

  // เตรียมข้อมูลสำหรับกราฟ
  const chartData = () => {
    const activeType = hoveredType || selectedType;
    if (activeType) {
      return receiptData
        .filter((item) => item.LivestockType === activeType)
        .map((item) => ({
          name: item.VaccineNameTH,
          quantity: item.TotalQuantity,
          value: item.TotalValue,
          type: item.LivestockType,
        }));
    }
    return summaryData.map((item) => ({
      name:
        item.LivestockType === "POULTRY"
          ? "สัตว์ปีก"
          : item.LivestockType === "CATTLE"
          ? "โค-กระบือ"
          : "สุกร",
      quantity: item.Quantity,
      value: item.Value,
      type: item.LivestockType,
    }));
  };

  // คอลัมน์ตาราง
  const columns = [
    {
      name: "ประเภทปศุสัตว์",
      selector: (row) => row.LivestockType,
      sortable: true,
      cell: (row) => (
        <HStack spacing={2}>
          <Box
            w="12px"
            h="12px"
            bg={typeColors[row.LivestockType]}
            borderRadius="sm"
          />
          <Text>
            {row.LivestockType === "POULTRY"
              ? "สัตว์ปีก"
              : row.LivestockType === "CATTLE"
              ? "โค-กระบือ"
              : "สุกร"}
          </Text>
        </HStack>
      ),
    },
    {
      name: "จำนวนวัคซีน (โดส)",
      selector: (row) => row.Quantity,
      sortable: true,
      format: (row) => row.Quantity.toLocaleString("th-TH"),
    },
    {
      name: "มูลค่าวัคซีน (บาท)",
      selector: (row) => row.Value,
      sortable: true,
      format: (row) => row.Value.toLocaleString("th-TH"),
    },
  ];

  // Tooltip ที่กำหนดเอง
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid #ccc"
          borderRadius="md"
          boxShadow="md"
        >
          <Text>
            <strong>ชื่อ:</strong> {label}
          </Text>
          <Text>
            <strong>จำนวนวัคซีน:</strong>{" "}
            {data.quantity.toLocaleString("th-TH")} โดส
          </Text>
          <Text>
            <strong>มูลค่า:</strong> {data.value.toLocaleString("th-TH")} บาท
          </Text>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
      p={5}
      mt={6}
      bg="white"
    >
      <Box
        mb={5}
        borderRadius="full"
        w="100%"
        bgColor="iBlue.600"
        color="white"
        p={2}
      >
        <HStack spacing={5} ml={5}>
          <FontAwesomeIcon icon={faChartBar} color="white" size="xl" />
          <Heading size={{ base: "md", md: "md" }} fontWeight="normal">
            การรับวัคซีนเข้าคลังประจำเดือน มีนาคม 2568
          </Heading>
        </HStack>
      </Box>

      {/* ตาราง */}
      {summaryData.length > 0 && (
        <Box p={5} overflowX="auto">
          <DataTable
            columns={columns}
            data={summaryData}
            highlightOnHover
            striped
            responsive
            customStyles={tableCustomStyles}
            onRowMouseEnter={(row) => setHoveredType(row.LivestockType)}
            onRowMouseLeave={() => setHoveredType(null)}
          />
        </Box>
      )}

      {/* Filter */}
      <FormControl maxW="300px" m={5}>
        <FormLabel fontSize="xl" color="gray">
          เลือกประเภทปศุสัตว์
        </FormLabel>
        <Select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          placeholder="ทั้งหมด"
          colorScheme="iBlue"
        >
          <option value="POULTRY">สัตว์ปีก</option>
          <option value="CATTLE">โค-กระบือ</option>
          <option value="PIG">สุกร</option>
        </Select>
      </FormControl>

      {/* กราฟ */}
      {chartData().length > 0 ? (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={chartData()}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              interval={0}
              height={100}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              label={{
                value: "จำนวนวัคซีน (โดส)",
                angle: -90,
                position: "insideLeft",
                offset: -5,
              }}
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => value.toLocaleString("th-TH")}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              layout="horizontal"
              verticalAlign="top"
              align="right"
              wrapperStyle={{ paddingBottom: 10 }}
            />
            <Bar
              dataKey="quantity"
              name="จำนวนวัคซีน"
              barSize={50}
              fill="#16A085" // Fallback color
            >
              {chartData().map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={typeColors[entry.type] || "#16A085"}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Text textAlign="center" color="gray.500" p={5}>
          ไม่มีข้อมูลสำหรับแสดงกราฟ
        </Text>
      )}
    </Box>
  );
};

export default VaccineReceiptDashboard;
